import acceleratorOscillator from "./definitions/acceleratorOscillator";
import adx from "./definitions/adx";
import alligator from "./definitions/alligator";
import aroon from "./definitions/aroon";
import averageTrueRange from "./definitions/averageTrueRange";
import awesomeOscillator from "./definitions/awesomeOscillator";
import bearsPower from "./definitions/bearsPower";
import bollingerBands from "./definitions/bollingerBands";
import bollingerBandsWidth from "./definitions/bollingerBandsWidth";
import bullsPower from "./definitions/bullsPower";
import cci from "./definitions/cci";
import donchianChannel from "./definitions/donchianChannel";
import deMarker from "./definitions/deMarker";
import envelopes from "./definitions/envelopes";
import fractal from "./definitions/fractal";
import fractalChaosBands from "./definitions/fractalChaosBands";
import ichimoku from "./definitions/ichimoku";
import keltnerChannel from "./definitions/keltnerChannel";
import macd from "./definitions/macd";
import momentum from "./definitions/momentum";
import movingAverage from "./definitions/movingAverage";
import osma from "./definitions/osma";
import parabolicSar from "./definitions/parabolicSar";
import rsi from "./definitions/rsi";
import rateOfChange from "./definitions/rateOfChange";
import schaffTrendCycle from "./definitions/schaffTrendCycle";
import stochasticOscillator from "./definitions/stochasticOscillator";
import superTrend from "./definitions/superTrend";
import volume from "./definitions/volume";
import vortex from "./definitions/vortex";
import williamsR from "./definitions/williamsR";
import zigZag from "./definitions/zigZag";

/**
 * The 32 indicators of PO (research §1.4) in the order of its menu.
 * A definition:
 * - id, name (as in PO), isOscillator (drawn in its own pane), decimals of
 *   the pane scale (the pair precision when absent);
 * - inputs: { key, label, type: INPUT_TYPE, defaultValue, min, step, isInteger, options };
 * - plots: { key, label, type: PLOT_TYPE, color, downColor, colorRule, width, shift, between };
 *   colours are PALETTE names or THEME_COLOR values;
 * - levels: { value, color } of oscillators (lines in the pane, labels on its
 *   scale, a FILL plot with `between: FILL_BETWEEN_LEVELS` fills between the
 *   first and the last one);
 * - labels: signal labels on the chart { key, text, plotKey, isAbove }
 *   (drawIndicatorLabels), `key` is a buffer with the price, NaN elsewhere;
 * - buffers: keys of helper arrays in `series` besides the plots (state of
 *   recursive calculations, so a tick can calculate the last bar again);
 * - calculate(bars, inputs, series, fromIndex, context): writes the values from
 *   `fromIndex` on into `series` (an array per plot key and per `buffers`
 *   key); earlier values are already there; context: { priceStep } of the
 *   pair. May return a smaller index when it rewrote earlier values too.
 */
export const INDICATOR_DEFINITIONS = [
  acceleratorOscillator,
  adx,
  alligator,
  aroon,
  averageTrueRange,
  awesomeOscillator,
  bearsPower,
  bollingerBands,
  bollingerBandsWidth,
  bullsPower,
  cci,
  donchianChannel,
  deMarker,
  envelopes,
  fractal,
  fractalChaosBands,
  ichimoku,
  keltnerChannel,
  macd,
  momentum,
  movingAverage,
  osma,
  parabolicSar,
  rsi,
  rateOfChange,
  schaffTrendCycle,
  stochasticOscillator,
  superTrend,
  volume,
  vortex,
  williamsR,
  zigZag,
];

const DEFINITIONS_BY_ID = new Map(INDICATOR_DEFINITIONS.map((definition) => [definition.id, definition]));

/**
 * @param {string} id
 * @returns {object|null} the definition, null for an unknown id
 */
export const getIndicatorDefinition = (id) => DEFINITIONS_BY_ID.get(id) || null;
